import { useSnackbar } from "notistack";
import { useAuth } from "../../auth/FirebaseAuth";
import { useAppContext } from "../../config/AppContext";
import React, { useEffect, useState } from "react";
import {
  getDocDataById,
  getDocDataByRef,
  getOrgDataById,
  getUserDataById,
  queryCollection,
} from "../../models/DataFunctions";
import { transformLogsToTable } from "./adminHelpers";
import TextField2 from "../../textfields/TextField2";
import SecondaryButton from "../../buttons/SecondaryButton";
import PrimaryButton from "../../buttons/PrimaryButton";
import DataGridGPT2 from "../../tables/DataGridGPT2";
import { Divider } from "antd";
import Space from "../../layout/Space";
import Icon1 from "../../assets/icons/Icon";
import { EasySwitch } from "../../textfields/EasySwitch";
import TimePickerGPT0 from "../../textfields/TimePickerGPT0";

function Logs({ navigateWorkspace, handleLoginAs }) {
  const { currentUser, loading } = useAuth();
  const { appState, setAppState } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;
  const { enqueueSnackbar } = useSnackbar();
  const handleClickVariant = (message, variant) => {
    enqueueSnackbar(message, { variant });
  };

  const [onlyerrors, setOnlyErrors] = useState(true);

  const [userData, setUserData] = useState(null);
  const [orgData, setOrgData] = useState(null);

  const [searchValue, setSearchValue] = useState("");
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [options, setOptions] = useState([]);

  const [selected_log, setSelectedLog] = useState(null);
  const [count, setCount] = useState(1000);
  const [logs, setLogs] = useState([]);
  // Function to navigate to a client by ID
  const gotoUser = async (log) => {
    // Fetch the document data by reference
    const obj = await getDocDataById("log", log.id);
    setSelectedLog(obj);
  };

  const toggleOnlyErros = async () => {
    const before = onlyerrors;
    const newstate = !before;
    setOnlyErrors(newstate); // Update state based on Switch value
  };

  const [nopresigned, setNoPresigned] = useState(true);
  const [nolocalhost, setNoLocalhost] = useState(true);
  const toggleNoPresgined = async () => {
    const before = nopresigned;
    const newstate = !before;
    setNoPresigned(newstate); // Update state based on Switch value
  };

  const [noupdate, setNoUpdate] = useState(true);
  const toggleNoUpdate = async () => {
    const before = noupdate;
    const newstate = !before;
    setNoUpdate(newstate); // Update state based on Switch value
  };

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  const handleFilterByDate = async () => {
    if (startTime && endTime) {
      // moment date to date

      const startDate = new Date(startTime);
      const endDate = new Date(endTime);
      let filters = [
        {
          field: "date",
          operator: ">=",
          value: startDate,
        },
        {
          field: "date",
          operator: "<=",
          value: endDate,
        },
      ];
      // Fetch clients associated with the user's organization
      const logs = await queryCollection("log", filters, count, "date", "desc");
      // Transform clients into table format (rows and columns)
      const { rows, columns } = transformLogsToTable(
        logs,
        "",
        nopresigned,
        noupdate,
        nolocalhost
      );
      // Update state if the user data is new or changed
      setLogs(logs);
      setRows(rows);
      setColumns(columns);
    }
  };
  // Function to fetch data for the current user
  const handleFetchData = async () => {
    if (currentUser) {
      // Fetch user data by currentUser's ID
      const user = await getUserDataById(currentUser.uid);

      // Fetch organization data using the user's org reference
      const org = await getOrgDataById(user.org_id);
      let logs = [];
      let filters = [];
      if (onlyerrors) {
        filters = [
          {
            field: "status",
            operator: "in",
            value: ["500", "422", "403", "404", "401", "400"],
          },
        ];
        // Fetch clients associated with the user's organization
        logs = await queryCollection("log", filters, count, "date", "desc");
      } else {
        filters = [];
        // Fetch clients associated with the user's organization
        logs = await queryCollection("log", filters, count * 5, "date", "desc");
      }

      // Transform clients into table format (rows and columns)
      const { rows, columns } = transformLogsToTable(
        logs,
        "",
        nopresigned,
        noupdate,
        nolocalhost
      );

      // Update state if the user data is new or changed

      setUserData(user);
      setOrgData(org);
      setLogs(logs);
      setRows(rows);
      setColumns(columns);

      // Set options for the search bar (client names)
      setOptions(logs.map((log) => log.url));
    }
  };

  function highlightBaseUrl(text) {
    if (!text) {
      return <span></span>;
    }

    if (!text) {
      return <span></span>;
    }

    // Regular expression to match URLs followed by a double quote
    const urlRegex = /(https?:\/\/[^\s"]+)/g;

    // Replace the URL with the highlighted version
    const highlightedText = text.replace(urlRegex, (url) => {
      return `<span class="text-neutral-300">${url}</span>`;
    });

    // Render the result as HTML within a span
    return <span dangerouslySetInnerHTML={{ __html: highlightedText }} />;
  }

  function constructUrl(url) {
    if (!url) {
      return <div></div>;
    }

    // Define the base URLs to be highlighted in light grey
    const baseUrls = ["https://api.hexagone.expert", "http://127.0.0.1:8000"];

    // Extract the base URL from the input URL
    const baseUrl = baseUrls.find((base) => url.startsWith(base));

    let remainingUrl = url;

    if (baseUrl) {
      // Remove the base URL from the remaining URL
      remainingUrl = url.slice(baseUrl.length);
    }

    const url_parts = remainingUrl.split("/");

    const url_parts_colored = url_parts.map((part, index) => {
      // Check if it's the last part to avoid adding an extra "/"
      const isLastPart = index === url_parts.length - 1;

      // Apply conditional coloring for parts with length 20
      const partElement =
        part.length === 20 ? (
          <span key={index} className="text-pink-500">
            {part}
          </span>
        ) : (
          <span key={index} className="text-blue-500">
            {part}
          </span>
        );

      // If it's not the last part, add a slash after the part
      return (
        <React.Fragment key={index}>
          {partElement}
          {!isLastPart && <span className="text-black">/</span>}
        </React.Fragment>
      );
    });

    return (
      <div>
        {/* Add the base URL in light grey if it exists */}
        {baseUrl && (
          <span className="text-gray-400">
            {baseUrl}
            <span className="text-black"></span>
          </span>
        )}
        {url_parts_colored}
      </div>
    );
  }

  const handleLoginAsToFolder = async (folderid) => {
    const folder = await getDocDataById("folder", folderid);
    if (!folder) {
      console.error("Folder not found");
      handleClickVariant("Folder not found", "error");
      return;
    }
    const master = await getOrgDataById(folder.master_id);
    const user = await getUserDataById(master.users_id[0]);
    navigateWorkspace("/home");
    await handleLoginAs(user.id);
    setTimeout(() => navigateWorkspace("/folder/" + folderid), 2000);
  };

  const handleSearchChange = (valuee) => {
    let value = searchValue;
    if (valuee) {
      value = valuee;
    } else {
      value = "";
    }

    const { rows, columns } = transformLogsToTable(
      logs,
      value,
      nopresigned,
      noupdate
    );

    setRows(rows);
    setColumns(columns);
  };

  function getColor(status) {
    let textColor = "";
    let backgroundColor = "";
    if (status >= 500) {
      textColor = "#ff0000"; // Error status text color
      backgroundColor = "#ffcccc"; // Error status background
    } else if (status >= 400) {
      textColor = "#ff9900"; // Warning status text color
      backgroundColor = "#fff3e6"; // Warning status background
    } else {
      textColor = "#008000"; // Success status text color
      backgroundColor = "#e6ffe6"; // Success status background
    }
    return { textColor: textColor, backgroundColor: backgroundColor };
  }

  // Fetch data when the component mounts or when currentUser changes
  useEffect(() => {
    if (currentUser) {
      handleFetchData();
    }
  }, [currentUser, onlyerrors, nopresigned, noupdate]);

  // If the data is still loading or the user is not authenticated, show a loading message
  if (loading || !currentUser || !userData) {
    return <div></div>;
  }

  // Render the client data
  return (
    <div className="flex flex-col w-full h-screen p-4">
      <div className=" flex flex-row w-full h-full">
        <div className="flex flex-col w-full h-full max-w-full">
          <div className="text-hx font-medium w-full max-w-full flex flex-row gap-4">
            <div>Logs </div>
            <TextField2
              className="max-w-c100"
              label="Nombre de logs"
              value={count}
              onChange={(e) => setCount(e.target.value)}
            />
            <SecondaryButton
              onClick={() => {
                handleFetchData();
              }}
            >
              Apply
            </SecondaryButton>
          </div>
          <Space y={10} />
          <div className="flex flex-row w-full items-center">
            {/* Search Bar */}
            <div className="flex flex-row w-c700  gap-2 items-center">
              <TextField2
                label="Chercher"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
              <PrimaryButton
                disabled={searchValue == ""}
                className="h-fit "
                onClick={() => handleSearchChange(searchValue)}
              >
                Filtrer
              </PrimaryButton>
              <PrimaryButton
                className="h-fit "
                onClick={() => {
                  setSearchValue("");
                  handleSearchChange(null);
                }}
              >
                Reset
              </PrimaryButton>
            </div>

            <div className="text-h3 text-shadow_50 flex flex-col ml-4 bg-white border-1 border-shadow_25 rounded-lg px-2">
              <div className="text-h3 text-shadow_50 flex flex-row gap-1 items-center justify-between">
                Uniquement les erreurs{" "}
                <EasySwitch checked={onlyerrors} onChange={toggleOnlyErros} />
              </div>{" "}
              <div className="text-h3 text-shadow_50 flex flex-row gap-1 items-center justify-between">
                No localhost{" "}
                <EasySwitch
                  checked={nolocalhost}
                  onChange={() => setNoLocalhost(!nolocalhost)}
                />
              </div>{" "}
              <div className="text-h3 text-shadow_50 flex flex-row gap-1 items-center justify-between">
                No presigned
                <EasySwitch
                  checked={nopresigned}
                  onChange={toggleNoPresgined}
                />
              </div>
              <div className="text-h3 text-shadow_50 flex flex-row gap-1 items-center justify-between">
                No update doc data field
                <EasySwitch checked={noupdate} onChange={toggleNoUpdate} />
              </div>
            </div>
          </div>
          <div className="flex flex-row gap-2">
            <TimePickerGPT0
              label="Heure de début"
              value={startTime}
              onChange={(newValue) => {
                setStartTime(newValue);
              }}
              className="max-w-c200"
            ></TimePickerGPT0>
            <TimePickerGPT0
              label="Heure de début"
              value={endTime}
              onChange={(newValue) => {
                setEndTime(newValue);
              }}
              className="max-w-c200"
            ></TimePickerGPT0>
            <SecondaryButton onClick={handleFilterByDate}>
              Filtrer seulement dans cette période
            </SecondaryButton>
          </div>

          {/* DataGrid displaying the clients */}
          <div className=" h-full w-full flex-grow pb-8 overflow-auto mb-4 mt-3">
            <div className="h-full  min-h-6">
              <DataGridGPT2
                rows={rows}
                columns={columns}
                onClick={gotoUser}
                initial_sort_field="date"
                initial_asc="desc"
              />
            </div>
          </div>
        </div>

        <div className="w-c500  h-full p-2 pb-10  mb-8 text-black ">
          {selected_log && (
            <div
              className="flex bg-white max-w-full flex-col border-l-4 h-full p-2 gap-1 text-h4 overflow-auto"
              style={{
                borderColor: getColor(selected_log.status).backgroundColor,
              }}
            >
              <div className="max-w-full font-bold flex flex-row gap-2">
                url
                <div
                  className="cursor-pointer"
                  onClick={() => {
                    navigator.clipboard.writeText(selected_log.url?.toString());
                  }}
                >
                  <Icon1 icon_name="copy" size={20} fill="grey"></Icon1>
                </div>
              </div>
              <div className="max-w-full whitespace-normal break-words">
                {selected_log.url?.toString()}
              </div>
              <div>{selected_log.status?.toString()}</div>
              <Space y={10} />
              <div className=" font-bold flex maw-w-c500 flex-row gap-2">
                body payload
                <div
                  className="cursor-pointer maw-w-c500"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      getJSONResponse(selected_log.request)
                    );
                  }}
                >
                  <Icon1 icon_name="copy" size={20} fill="grey"></Icon1>
                </div>
              </div>
              <div>
                {" "}
                {highlightBaseUrl(getJSONResponse(selected_log.request))}
              </div>
              <Space y={10} />
              <div className="font-bold flex flex-row gap-2">
                response
                <div
                  className="cursor-pointer"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      getJSONResponse(selected_log.response)
                    );
                  }}
                >
                  <Icon1 icon_name="copy" size={20} fill="grey"></Icon1>
                </div>
              </div>
              <div> {getJSONResponse(selected_log.response)}</div>
              <Space y={10} />
              <div className="font-bold">author</div>
              <div className=" flex flex-row gap-2">
                email
                <div
                  className="cursor-pointer"
                  onClick={() => {
                    navigator.clipboard.writeText(selected_log.email);
                  }}
                >
                  <Icon1 icon_name="copy" size={20} fill="grey"></Icon1>
                </div>
                {selected_log.email}
              </div>
              <div className=" flex flex-row gap-2">
                user_id
                <div
                  className="cursor-pointer"
                  onClick={() => {
                    navigator.clipboard.writeText(selected_log.user_id);
                  }}
                >
                  <Icon1 icon_name="copy" size={20} fill="grey"></Icon1>
                </div>
                {selected_log.user_id}
              </div>
              <div className=" flex flex-row gap-2">
                org_id
                <div
                  className="cursor-pointer"
                  onClick={() => {
                    navigator.clipboard.writeText(selected_log.org_id);
                  }}
                >
                  <Icon1 icon_name="copy" size={20} fill="grey"></Icon1>
                </div>
                {selected_log.org_id}
              </div>
              <Space y={10} />
              {selected_log.folderid && (
                <PrimaryButton
                  onClick={() => handleLoginAsToFolder(selected_log.folderid)}
                  className="w-fit"
                >
                  Login As to Folder
                </PrimaryButton>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Logs;

export const getJSONResponse = (response) => {
  // if reponse is an object, make it a json string
  if (typeof response === "object") {
    return JSON.stringify(response, null, 2);
  }
  // if response is a string, return it as is
  if (typeof response === "string") {
    return response;
  }
};
